import { containerQueries } from "@/features/containers/api/containers-keys";
// @wc-ignore-file
import type { QueryClient } from "@tanstack/react-query";
import { notFound, redirect } from "@tanstack/react-router";
import { DocumentNotFound } from "./components/document-not-found";
import { type DocSearch, validateDocSearch } from "./utils/doc-search";
import {
  type GetDocumentRouteContainer,
  resolveDocumentRouteParams,
} from "./utils/resolve-document-route";

/**
 * CR-162: the bare `/docs/:id` URL has no project in its match tree, so the
 * document page cannot mount there. Links of that shape still exist in old AI
 * messages and emails; this sends them to the document's project URL
 * (replacing the history entry, so Back does not bounce through `/docs/:id`),
 * or to the not-found state when the document cannot be resolved.
 */

/** Always throws: a redirect to the project-scoped document URL, or notFound(). */
export async function redirectBareDocument({
  docId,
  search,
  getContainer,
}: {
  docId: string;
  search: DocSearch;
  getContainer: GetDocumentRouteContainer;
}): Promise<never> {
  const route = await resolveDocumentRouteParams(getContainer, docId);
  if (!route) throw notFound();
  throw redirect({
    to: "/workspaces/$slug/projects/$projectSlug/docs/$docId",
    params: { slug: route.slug, projectSlug: route.projectSlug, docId },
    search: {
      mentionUserId: search.mentionUserId,
      commentId: search.commentId,
    },
    replace: true,
  });
}

/**
 * Options of the component-less `/_authenticated/docs/$docId` route, spread by
 * the route file and by its router test. The loader (not beforeLoad) throws,
 * so notFound() renders this route's own notFoundComponent.
 */
export const bareDocRouteOptions = {
  validateSearch: validateDocSearch,
  loaderDeps: ({ search }: { search: DocSearch }) => search,
  loader: ({
    context,
    params,
    deps,
  }: {
    context: { queryClient: Pick<QueryClient, "ensureQueryData"> };
    params: { docId: string };
    deps: DocSearch;
  }) =>
    redirectBareDocument({
      docId: params.docId,
      search: deps,
      // retry: false — a missing or RLS-hidden row is a deterministic
      // `.single()` error; retrying only delays the not-found state.
      getContainer: (id) =>
        context.queryClient.ensureQueryData({ ...containerQueries.byId(id), retry: false }),
    }),
  notFoundComponent: DocumentNotFound,
};
